'use client';

import { Modal, ModalBody, ModalContent, ModalHeader, User } from '@heroui/react';
import React, { useState } from 'react';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import useFormatDate from '@/hooks/use-format-date';
import { DATE_FORMAT } from '@/utils/format-date';
import RatingStars from '../../rating-stars/rating-stars';

export type ReviewImage = {
  id: string;
  url: string;
};

export type ReviewType = {
  user: {
    name: string;
    avatar: string;
  };
  createdAt?: string;
  rating: number;
  title: string;
  content: string;
  images?: ReviewImage[];
};

type ReviewProps = React.HTMLAttributes<HTMLDivElement> & ReviewType;

const Review = React.forwardRef<HTMLDivElement, ReviewProps>(
  ({ children, user, title, content, rating, createdAt, images, ...props }, ref) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const { formatDateOnly } = useFormatDate();

    return (
      <div ref={ref} {...props}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User
              avatarProps={{
                src: user.avatar || undefined,
                name: user.name,
                showFallback: true,
                classNames: { name: 'font-bold' },
                getInitials: name =>
                  name
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map(part => part[0]?.toUpperCase() ?? '')
                    .join(''),
              }}
              classNames={{
                name: 'font-medium',
                description: 'text-small',
              }}
              description={createdAt ? formatDateOnly(createdAt, DATE_FORMAT) : undefined}
              name={user.name}
            />
          </div>
          <RatingStars rating={rating} starClassName="text-lg sm:text-xl" />
        </div>
        <div className="mt-4 w-full">
          <p className="text-default-900 font-medium">{title}</p>
          <p className="text-default-700 mt-2">{content}</p>
          {images && images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {images.map(image => (
                <button
                  key={image.id}
                  type="button"
                  onClick={() => setPreviewUrl(image.url)}
                  className="rounded-medium border-default-200 hover:border-default-400 h-20 w-20 overflow-hidden border transition-colors"
                >
                  <RenderableImage
                    src={image.url}
                    alt={title}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
          {children}
        </div>
        <Modal isOpen={!!previewUrl} size="2xl" onOpenChange={open => !open && setPreviewUrl(null)}>
          <ModalContent>
            <ModalHeader className="flex flex-col gap-1">{title}</ModalHeader>
            <ModalBody className="pb-6">
              {previewUrl && (
                <RenderableImage
                  src={previewUrl}
                  alt={title}
                  className="max-h-[80vh] w-full object-contain"
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </div>
    );
  },
);

Review.displayName = 'Review';

export default Review;
